// ui-top-jump-search-controller.ts
// (Assuming this is the controller for packs/ui/...)

import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'
import { Turbo } from '@hotwired/turbo-rails'

const MIN_INPUT_LENGTH_FOR_SEARCH = 3
const DEBOUNCE_DELAY_MS = 500
const RESULT_SELECTED_CLASS = 'via-top-jump-selected'
const LOADING_CLASSES = ['opacity-60', 'pointer-events-none']

export default class extends Controller<HTMLFormElement> {
  static targets = ['input', 'resultPopup', 'resultLink', 'loadingContainer', 'loadingSpinner']

  declare readonly inputTarget: HTMLInputElement
  declare readonly resultPopupTarget: HTMLElement
  declare readonly resultLinkTargets: HTMLAnchorElement[]
  declare readonly hasResultLinkTarget: boolean
  declare readonly hasLoadingContainerTarget: boolean
  declare readonly loadingContainerTarget: HTMLElement
  declare readonly loadingSpinnerTarget: HTMLElement

  declare triggerFormSubmit: () => void

  initialize(): void {
    this.triggerFormSubmit = debounce(() => this.element.requestSubmit(), DEBOUNCE_DELAY_MS)
  }

  connect(): void {
    this.resultPopupTarget.addEventListener('turbo:before-fetch-request', this.showLoading)
    this.resultPopupTarget.addEventListener('turbo:frame-load', this.hideLoading)
  }

  disconnect(): void {
    this.resultPopupTarget.removeEventListener('turbo:before-fetch-request', this.showLoading)
    this.resultPopupTarget.removeEventListener('turbo:frame-load', this.hideLoading)
  }

  inputChanged = (): void => {
    this.sanitizeInput()
    this.hasEnoughText ? this.showResults() : this.hideResults()
    if (this.hasEnoughText) this.triggerFormSubmit()
  }

  inputKeyPressed = (event: KeyboardEvent): void => {
    const actions: Record<string, () => void> = {
      ArrowUp: () => this.navigateResults(-1),
      ArrowDown: () => this.navigateResults(1),
      Enter: () => this.visitSelectedLink()
    }

    const action = actions[event.key]
    if (action) {
      event.preventDefault()
      action()
    }
  }

  handleFocus = (): void => {
    if (this.hasEnoughText) this.showResults()
  }

  handleClickOutside = (event: MouseEvent): void => {
    const target = event.target as Node
    if (!this.element.contains(target) && !this.resultPopupTarget.contains(target)) {
      this.hideResults()
    }
  }

  handleGlobalKeydown = (event: KeyboardEvent): void => {
    const isPasteHotkey =
      event.key === 'v' && (event.metaKey || event.ctrlKey) && event.target === document.body
    if (isPasteHotkey) {
      this.inputTarget.value = ''
      this.inputTarget.focus()
    }
  }

  keepFocus = (): void => {
    this.inputTarget.focus()
  }

  private showLoading = (): void => {
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.add(...LOADING_CLASSES)
    }
    this.loadingSpinnerTarget.classList.remove('hidden')
  }

  private hideLoading = (): void => {
    if (this.hasLoadingContainerTarget) {
      this.loadingContainerTarget.classList.remove(...LOADING_CLASSES)
    }
    this.loadingSpinnerTarget.classList.add('hidden')
  }

  private showResults = (): void => this.resultPopupTarget.classList.remove('hidden')

  private hideResults = (): void => this.resultPopupTarget.classList.add('hidden')

  private get hasEnoughText(): boolean {
    return this.inputTarget.value.length >= MIN_INPUT_LENGTH_FOR_SEARCH
  }

  private sanitizeInput(): void {
    this.inputTarget.value = this.inputTarget.value.toUpperCase().replace(/ /g, '')
  }

  private get selectedResultLink(): HTMLAnchorElement | undefined {
    return this.resultLinkTargets.find(link => link.classList.contains(RESULT_SELECTED_CLASS))
  }

  private selectResultLink(linkToSelect: HTMLAnchorElement): void {
    this.resultLinkTargets.forEach(link => link.classList.remove(RESULT_SELECTED_CLASS))
    linkToSelect.classList.add(RESULT_SELECTED_CLASS)
    linkToSelect.scrollIntoView({ block: 'nearest' })
  }

  private navigateResults(increment: 1 | -1): void {
    const result_links = this.resultLinkTargets
    if (result_links.length === 0) return

    // If nothing is selected, start from -1
    const currentIndex = this.selectedResultLink
      ? result_links.indexOf(this.selectedResultLink)
      : -1
    // Calculate new index with wrap-around
    const newIndex = (currentIndex + increment + result_links.length) % result_links.length

    this.selectResultLink(result_links[newIndex])
  }

  private visitSelectedLink(): void {
    this.selectedResultLink?.click()
  }
}
